import { AgGridReact } from "ag-grid-react";
// import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { blockGridStylesTradeCreation } from "../Creation/helper/tradeCreationHelper";
import useTableUIStore from "@/store/tableUIStore";

import {
  GetContextMenuItemsParams,
  GetRowIdParams,
  MenuItemDef,
} from "ag-grid-community";
import {
  debounce,
  getFontSizePx,
  getHeaderHeight,
  getRowHeight,
} from "../tradeActionSummary/helper/actionSummaryHelper";
import {
  defaultColDefAxeCreation,
  returnAxeCreationColDefs,
} from "./helper/axeCreationColDefsHelper";

import {
  applyColumnStateToAGGridTable,
  applyFiltersToAGGridTable,
  manageGridEventListeners,
  readGridRefAndUpdateStoreAndDatabaseWithNewValues,
} from "@/lib/AGGridHelpers";
import { returnOpenWidgetsObject } from "@/lib/widgetHelperFunctions";
import { clearFilters } from "@/components/icons/SVGIcons";
import { axeCreationSidebar } from "./helper/axeCreationSideBarHelper";
import GenerateAxeSidebarContent from "./GenerateAxeSidebarContent";

import { IAxeCreationData } from "@/lib/interfaces and Enums/InterfaceAxes";

interface IAxeCreationTable {
  cryptoID?: string | undefined;
  theme: string | undefined;
  getAccessTokenSilently: Function;
  axeCreationData: IAxeCreationData[]; //any[];
}

const AxeCreationTable = ({
  cryptoID,
  theme,
  getAccessTokenSilently,
  axeCreationData,
}: IAxeCreationTable) => {
  function getContextMenuItems(params: GetContextMenuItemsParams) {
    var result: (string | MenuItemDef)[] = [
      "cut",
      "copy",
      "copyWithHeaders",
      "copyWithGroupHeaders",
      "paste",
      "separator",
      "export",
      "separator",
      {
        name: "Clear Filters",
        icon: clearFilters,
        action: () => {
          params.api.setFilterModel(null);
        },
      },
    ];
    return result;
  }

  const tableStripeOddTrue = useTableUIStore(
    (state) => state.tableStripeImportOn,
  ); // used to change the table stripe with settings.
  const fontSize = useTableUIStore((state) => state.fontSize);
  const rowHeightSetting = useTableUIStore((state) => state.rowHeight);
  const creationFlashColorLight = useTableUIStore(
    (state) => state.creationFlashColorLight,
  );
  const creationFlashColorDark = useTableUIStore(
    (state) => state.creationFlashColorDark,
  );

  const [rowData, setRowData] = useState(axeCreationData);
  useEffect(() => {
    setRowData(axeCreationData);
  }, [axeCreationData]);

  const gridRef = useRef<AgGridReact>(null);

  const coldefs = useMemo(() => {
    return returnAxeCreationColDefs(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  const getRowId = useCallback((params: GetRowIdParams) => {
    return params.data.axeId;
  }, []);

  const sideBar = useMemo(() => {
    return axeCreationSidebar({ theme, getAccessTokenSilently });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);
  //   // -- START of persisting table state to the widget -- //

  const debouncedSaveColumnState = debounce(() => {
    readGridRefAndUpdateStoreAndDatabaseWithNewValues(
      gridRef,
      getAccessTokenSilently,
      cryptoID,
    );
  }, 1000);

  const onGridReady = useCallback(async () => {
    const openWidgetObject = returnOpenWidgetsObject(cryptoID);
    applyColumnStateToAGGridTable(gridRef, openWidgetObject);

    if (gridRef.current && openWidgetObject.filterModel) {
      applyFiltersToAGGridTable(gridRef, openWidgetObject);
    }

    // apply the Event listeners form AG Grid Helpers
    const cleanupEventListeners = manageGridEventListeners(
      gridRef,
      debouncedSaveColumnState,
    );
    return cleanupEventListeners; // Cleanup on unmount or dependency change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSaveColumnState]);

  //   // -- END of persisting table state to the widget -- //

  const dynamicRowHeight = useMemo(() => getRowHeight(rowHeightSetting), [rowHeightSetting]);
  const dynamicHeaderHeight = useMemo(() => getHeaderHeight(rowHeightSetting), [rowHeightSetting]);

  const style = useMemo(
    () => ({
      ...(blockGridStylesTradeCreation(
        theme,
        tableStripeOddTrue,
      ) as React.CSSProperties),
      fontSize: `${getFontSizePx(fontSize)}px`,
    }),
    [theme, tableStripeOddTrue, fontSize, creationFlashColorLight, creationFlashColorDark],
  );

  const popupParent = useMemo<HTMLElement | null>(() => {
    return document.querySelector(".GridContainer");
  }, []);

  return (
    <div className="flex h-full w-full flex-col">
      {/* <div className="mt-1 flex space-x-3 py-2 ">
        <Button
          variant="mainBlueAction"
          size={"button_sm_mainBlueAction"}
          data-testid="selectPanelBtn"
          onClick={() => getSelectedRowData()}
        >
          Create Axe
        </Button>
        <Button
          variant="secondaryBlueActionAtTradeCreation"
          size={"button_sm_mainBlueAction"}
          data-testid="saveTradeBtn"
        >
          Delete Axe
        </Button>
      </div> */}
      <div className="flex w-full flex-grow">
        <div
          className={`${theme == "dark" ? "ag-theme-balham-dark" : "ag-theme-balham"} h-full w-full border-l-2 border-[#E7EAEB] dark:border-[#252525]`}
          style={style}
        >
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={coldefs}
            columnMenu={"new"} //do not delete, affects header menus - zallows the filter icon to be visible
            defaultColDef={defaultColDefAxeCreation}
            suppressAggFuncInHeader={true}
            valueCache={true}
            getRowId={getRowId}
            rowSelection={"multiple"}
            suppressRowClickSelection={true}
            rowGroupPanelShow={"always"}
            groupDisplayType={"groupRows"} // removed the left hand side checkbox.
            sideBar={sideBar}
            components={{
              customConfigsToolPanel: GenerateAxeSidebarContent,
            }}
            onGridReady={onGridReady}
            scrollbarWidth={6}
            getContextMenuItems={getContextMenuItems}
            rowHeight={dynamicRowHeight}
            headerHeight={dynamicHeaderHeight}
            singleClickEdit={true}
            stopEditingWhenCellsLoseFocus={true}
            popupParent={popupParent}
          />
        </div>
      </div>
    </div>
  );
};

export default AxeCreationTable;
